import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "../FunnelLayout";
import { useFunnelForm } from "../useFunnelForm";
import { createLead } from "../api";

function LocationPinIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function LockIcon({ size = 16 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 12 15 16 10" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export default function Step1() {
  const navigate = useNavigate();
  const { data, update, setLead } = useFunnelForm();
  const [zip, setZip] = useState(data.step1?.zip || "");
  const [error, setError] = useState("");
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleZipChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 5);
    setZip(value);
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (zip.length !== 5) {
      setError("Please enter a valid 5-digit ZIP code.");
      return;
    }
    if (submitting) return;

    setApiError("");
    setSubmitting(true);
    update("step1", { zip });
    try {
      const res = await createLead({
        step1: { zip },
        tracking: data.tracking,
        pageUrl: window.location.href
      });
      setLead(res.lead_id, res.created_at);
      navigate("/v2/step-2");
    } catch (err) {
      setApiError("We couldn't save your info. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <FunnelLayout currentStep={1} showProgress={false} wide>
      <div className="fv2-step1">
        <div className="fv2-step1-grid">
          <div className="fv2-step1-left">
            <h1 className="fv2-step1-h1">
              <span className="fv2-step1-h1-line fv2-step1-h1-navy">Compare Family</span>
              <span className="fv2-step1-h1-line fv2-step1-h1-navy">Health Plan</span>
              <span className="fv2-step1-h1-line fv2-step1-h1-green">Options</span>
              <span className="fv2-step1-h1-line fv2-step1-h1-navy">In Your Area</span>
            </h1>

            <p className="fv2-step1-sub">
              Check available health plan options in your area and get expert help
              comparing the best fit for your family.
            </p>

            <form onSubmit={handleSubmit} className="fv2-step1-form" noValidate>
              <label htmlFor="fv2-zip" className="fv2-step1-label">
                See plans available in your area
              </label>

              <div className="fv2-step1-input-wrap">
                <span className="fv2-step1-input-icon">
                  <LocationPinIcon />
                </span>
                <input
                  id="fv2-zip"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  placeholder="Enter your ZIP code"
                  value={zip}
                  onChange={handleZipChange}
                  aria-invalid={error ? "true" : "false"}
                  aria-describedby={error ? "fv2-zip-error" : undefined}
                />
              </div>

              {error && (
                <p id="fv2-zip-error" className="fv2-step1-error" role="alert">
                  {error}
                </p>
              )}

              <button type="submit" className="fv2-step1-submit" disabled={submitting}>
                {submitting ? "Saving..." : "See My Options"}
                <ArrowRightIcon />
              </button>

              {apiError && (
                <p className="fv2-step1-error" role="alert">
                  {apiError}
                </p>
              )}

              <p className="fv2-step1-secure">
                <LockIcon /> Your information is secure and private.
              </p>
            </form>
          </div>

          <div className="fv2-step1-right" aria-hidden="true">
            <img src="/v2-photo.jpg" alt="Happy family at home" className="fv2-step1-hero-image" />
          </div>
        </div>

        <div className="fv2-step1-trust-card">
          <div className="fv2-step1-trust-card-head">
            <span className="fv2-step1-trust-card-check">
              <CheckIcon />
            </span>
            <strong>Complete Step 1 to continue to your coverage questions.</strong>
          </div>
          <p>
            By continuing, you can request information about available health
            coverage options from licensed representatives. There is no obligation
            to enroll.
          </p>
          <p>
            FamilyHealth Plans is not a government website and is not affiliated
            with Healthcare.gov, Medicare, Medicaid, any federal marketplace, or
            any state marketplace.
          </p>
        </div>

        <div className="fv2-step1-trust-strip">
          <div className="fv2-step1-trust-item">
            <div className="fv2-step1-trust-icon fv2-step1-trust-icon-blue">
              <ShieldIcon />
            </div>
            <h3 className="fv2-step1-trust-h-blue">Licensed Agents</h3>
            <p>Get help from licensed professionals.</p>
          </div>

          <div className="fv2-step1-trust-item">
            <div className="fv2-step1-trust-icon fv2-step1-trust-icon-green">
              <LockIcon size={26} />
            </div>
            <h3 className="fv2-step1-trust-h-green">Secure &amp; Private</h3>
            <p>Your information is safe and encrypted.</p>
          </div>

          <div className="fv2-step1-trust-item">
            <div className="fv2-step1-trust-icon fv2-step1-trust-icon-blue">
              <CheckCircleIcon />
            </div>
            <h3 className="fv2-step1-trust-h-blue">No Obligation</h3>
            <p>No obligation to enroll. You choose.</p>
          </div>
        </div>

        <div className="fv2-step1-info-bar">
          <span className="fv2-step1-info-bar-icon">
            <ClockIcon />
          </span>
          <div className="fv2-step1-info-bar-text">
            <strong>Compare options in minutes.</strong>
            <p>No obligation to enroll. Licensed agents available if you want help.</p>
          </div>
        </div>
      </div>
    </FunnelLayout>
  );
}
